import { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';

const recruitmentTransportLocations = [
  { id: 1, name: "Bangkok, Thailand", coordinates: [100.5018, 13.7563], type: "Recruitment Hub", description: "Major recruitment center in Southeast Asia" },
  { id: 2, name: "Mae Sot, Thailand", coordinates: [98.5706, 16.7157], type: "Transit Point", description: "Border town used for crossing into Myanmar" },
  { id: 3, name: "Myawaddy, Myanmar", coordinates: [98.5089, 16.6867], type: "Destination", description: "Location of several scam compounds" },
  { id: 4, name: "Nairobi, Kenya", coordinates: [36.8219, -1.2921], type: "Recruitment Hub", description: "Major recruitment point in East Africa" },
  { id: 5, name: "Lagos, Nigeria", coordinates: [3.3792, 6.5244], type: "Recruitment Hub", description: "Major recruitment point in West Africa" },
  { id: 6, name: "Manila, Philippines", coordinates: [120.9842, 14.5995], type: "Recruitment Hub", description: "Major recruitment center in Southeast Asia" },
  { id: 7, name: "Phnom Penh, Cambodia", coordinates: [104.9282, 11.5564], type: "Destination", description: "Location of multiple scam compounds" },
  { id: 8, name: "Dubai, UAE", coordinates: [55.2708, 25.2048], type: "Transit Point", description: "Common transit point for victims from Africa and South Asia" }
];

const traffickingRoutes = [
  { source: 4, target: 8 },
  { source: 5, target: 8 },
  { source: 8, target: 1 },
  { source: 6, target: 1 },
  { source: 1, target: 2 },
  { source: 2, target: 3 },
  { source: 1, target: 7 }
];

const colorMap = {
  "Recruitment Hub": "#F87171",
  "Transit Point": "#FBBF24",
  "Destination": "#60A5FA"
};

const RecruitmentMap = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [worldData, setWorldData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  const width = window.innerWidth;
  const height = window.innerHeight;

  useEffect(() => {
    const fetchWorldData = async () => {
      try {
        const response = await fetch('https://unpkg.com/world-atlas@2.0.2/countries-110m.json');
        const data = await response.json();
        setWorldData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load world map data:", error);
        setIsLoading(false);
      }
    };
    fetchWorldData();
  }, []);

  useEffect(() => {
    if (!worldData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    const tooltip = d3.select(tooltipRef.current);

    const projection = d3.geoMercator()
      .scale(width / 6.2)
      .translate([width / 2, height / 1.6]);

    const path = d3.geoPath().projection(projection);
    const countries = topojson.feature(worldData, worldData.objects.countries);

    svg.append("g")
      .attr("class", "countries")
      .selectAll("path")
      .data(countries.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("fill", "#e5e5e5")
      .attr("stroke", "#fff")
      .attr("stroke-width", 0.5);

    const routeGroup = svg.append("g").attr("class", "routes");
    traffickingRoutes.forEach(route => {
      const source = recruitmentTransportLocations.find(loc => loc.id === route.source);
      const target = recruitmentTransportLocations.find(loc => loc.id === route.target);
      if (source && target) {
        const sourcePos = projection(source.coordinates);
        const targetPos = projection(target.coordinates);
        const dx = targetPos[0] - sourcePos[0];
        const dy = targetPos[1] - sourcePos[1];
        const dr = Math.sqrt(dx * dx + dy * dy) * 1.2;

        routeGroup.append("path")
          .attr("d", `M${sourcePos[0]},${sourcePos[1]}A${dr},${dr} 0 0,1 ${targetPos[0]},${targetPos[1]}`)
          .attr("fill", "none")
          .attr("stroke", "#6B7280")
          .attr("stroke-width", 1.5)
          .attr("stroke-dasharray", "5,5")
          .attr("opacity", 0.7);
      }
    });

    const markerGroup = svg.append("g").attr("class", "markers");
    recruitmentTransportLocations.forEach(location => {
      const [x, y] = projection(location.coordinates);
      const marker = markerGroup.append("g")
        .attr("transform", `translate(${x}, ${y})`)
        .attr("class", "marker")
        .style("cursor", "pointer");

      marker.append("circle").attr("r", 8).attr("fill", "white");
      marker.append("circle").attr("r", 6).attr("fill", colorMap[location.type] || "#4B5563");

      marker.on("mouseover", (event) => {
        d3.select(event.currentTarget).select("circle:first-child").transition().duration(300).attr("r", 10);
        d3.select(event.currentTarget).select("circle:nth-child(2)").transition().duration(300).attr("r", 8);
        const [mouseX, mouseY] = d3.pointer(event);
        tooltip.style("left", `${mouseX + 20}px`).style("top", `${mouseY}px`).style("opacity", 1);
        setSelectedLocation(location);
      });

      marker.on("mouseout", () => {
        d3.select(marker.node()).select("circle:first-child").transition().duration(300).attr("r", 8);
        d3.select(marker.node()).select("circle:nth-child(2)").transition().duration(300).attr("r", 6);
        tooltip.style("opacity", 0);
        setSelectedLocation(null);
      });
    });

    const legend = svg.append("g").attr("transform", `translate(20, ${height - 85})`);
    ["Recruitment Hub", "Transit Point", "Destination"].forEach((type, i) => {
      const legendItem = legend.append("g").attr("transform", `translate(0, ${i * 25})`);
      legendItem.append("circle").attr("r", 6).attr("fill", colorMap[type]);
      legendItem.append("text").attr("x", 15).attr("y", 4).text(type).attr("font-size", "12px").attr("fill", "#4B5563");
    });

    svg.append("text")
      .attr("x", width / 2)
      .attr("y", 30)
      .attr("text-anchor", "middle")
      .attr("font-size", "18px")
      .attr("font-weight", "bold")
      .text("Trafficking Routes & Hotspots");
  }, [worldData]);

  if (isLoading) {
    return <div className="w-screen h-screen flex items-center justify-center bg-gray-100"><p className="text-gray-500">Loading map...</p></div>;
  }

  return (
    <div className="relative w-screen h-screen bg-white overflow-hidden">
      <svg
        ref={svgRef}
        className="absolute top-0 left-0 w-full h-full"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
      />
      <div
        ref={tooltipRef}
        className="absolute pointer-events-none bg-white p-3 rounded shadow-md border border-gray-300 max-w-xs opacity-0 z-10"
        style={{ transition: 'opacity 0.3s ease' }}
      >
        {selectedLocation && (
          <>
            <h3 className="font-semibold text-md mb-1">{selectedLocation.name}</h3>
            <p className="text-sm text-gray-600">{selectedLocation.description}</p>
            <div
              className="mt-2 inline-block px-2 py-1 text-xs font-semibold rounded-full"
              style={{
                backgroundColor: `${colorMap[selectedLocation.type]}22`,
                color: colorMap[selectedLocation.type]
              }}
            >
              {selectedLocation.type}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecruitmentMap;



// Below is a working code but with full-page display issues.

// import { useState, useEffect, useRef } from 'react';
// import * as d3 from 'd3';
// import * as topojson from 'topojson-client';

// // Define recruitment transport locations directly in the component
// const recruitmentTransportLocations = [
//   {
//     id: 1,
//     name: "Bangkok, Thailand",
//     coordinates: [100.5018, 13.7563],
//     type: "Recruitment Hub",
//     description: "Major recruitment center in Southeast Asia"
//   },
//   {
//     id: 2,
//     name: "Mae Sot, Thailand",
//     coordinates: [98.5706, 16.7157],
//     type: "Transit Point",
//     description: "Border town used for crossing into Myanmar"
//   },
//   {
//     id: 3,
//     name: "Myawaddy, Myanmar",
//     coordinates: [98.5089, 16.6867],
//     type: "Destination",
//     description: "Location of several scam compounds"
//   },
//   {
//     id: 4,
//     name: "Nairobi, Kenya",
//     coordinates: [36.8219, -1.2921],
//     type: "Recruitment Hub",
//     description: "Major recruitment point in East Africa"
//   },
//   {
//     id: 5,
//     name: "Lagos, Nigeria",
//     coordinates: [3.3792, 6.5244],
//     type: "Recruitment Hub",
//     description: "Major recruitment point in West Africa"
//   },
//   {
//     id: 6,
//     name: "Manila, Philippines",
//     coordinates: [120.9842, 14.5995],
//     type: "Recruitment Hub",
//     description: "Major recruitment center in Southeast Asia"
//   },
//   {
//     id: 7,
//     name: "Phnom Penh, Cambodia",
//     coordinates: [104.9282, 11.5564],
//     type: "Destination",
//     description: "Location of multiple scam compounds"
//   },
//   {
//     id: 8,
//     name: "Dubai, UAE",
//     coordinates: [55.2708, 25.2048],
//     type: "Transit Point",
//     description: "Common transit point for victims from Africa and South Asia"
//   }
// ];

// // Define the main routes between locations (source to destination)
// const traffickingRoutes = [
//   { source: 4, target: 8 }, // Nairobi to Dubai
//   { source: 5, target: 8 }, // Lagos to Dubai
//   { source: 8, target: 1 }, // Dubai to Bangkok
//   { source: 6, target: 1 }, // Manila to Bangkok
//   { source: 1, target: 2 }, // Bangkok to Mae Sot
//   { source: 2, target: 3 }, // Mae Sot to Myawaddy
//   { source: 1, target: 7 }  // Bangkok to Phnom Penh
// ];

// const colorMap = {
//   "Recruitment Hub": "#F87171", // Red
//   "Transit Point": "#FBBF24",   // Yellow
//   "Destination": "#60A5FA"      // Blue
// };

// const RecruitmentMap = () => {
//   const [selectedLocation, setSelectedLocation] = useState(null);
//   const [worldData, setWorldData] = useState(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const svgRef = useRef(null);
//   const tooltipRef = useRef(null);
//   const width = 900;
//   const height = 480;

//   useEffect(() => {
//     const fetchWorldData = async () => {
//       try {
//         const response = await fetch('https://unpkg.com/world-atlas@2.0.2/countries-110m.json');
//         const data = await response.json();
//         setWorldData(data);
//         setIsLoading(false);
//       } catch (error) {
//         console.error("Failed to load world map data:", error);
//         setIsLoading(false);
//       }
//     };

//     fetchWorldData();
//   }, []);

//   useEffect(() => {
//     if (!worldData) return;

//     const svg = d3.select(svgRef.current);
//     svg.selectAll("*").remove();

//     // Create a tooltip
//     const tooltip = d3.select(tooltipRef.current);

//     // Set up the projection
//     const projection = d3.geoMercator()
//       .scale(140)
//       .translate([width / 2, height / 1.5]);

//     // Create a path generator
//     const path = d3.geoPath()
//       .projection(projection);

//     // Draw the map
//     const countries = topojson.feature(worldData, worldData.objects.countries);
//     svg.append("g")
//       .attr("class", "countries")
//       .selectAll("path")
//       .data(countries.features)
//       .enter()
//       .append("path")
//       .attr("d", path)
//       .attr("fill", "#e5e5e5")
//       .attr("stroke", "#fff")
//       .attr("stroke-width", 0.5);

//     // Draw routes
//     const routeGroup = svg.append("g")
//       .attr("class", "routes");

//     traffickingRoutes.forEach(route => {
//       const source = recruitmentTransportLocations.find(loc => loc.id === route.source);
//       const target = recruitmentTransportLocations.find(loc => loc.id === route.target);
      
//       if (source && target) {
//         const sourcePos = projection(source.coordinates);
//         const targetPos = projection(target.coordinates);
        
//         // Create a curved line between source and target
//         const dx = targetPos[0] - sourcePos[0];
//         const dy = targetPos[1] - sourcePos[1];
//         const dr = Math.sqrt(dx * dx + dy * dy) * 1.2;
        
//         routeGroup.append("path")
//           .attr("d", `M${sourcePos[0]},${sourcePos[1]}A${dr},${dr} 0 0,1 ${targetPos[0]},${targetPos[1]}`)
//           .attr("fill", "none")
//           .attr("stroke", "#6B7280")
//           .attr("stroke-width", 1.5)
//           .attr("stroke-dasharray", "5,5")
//           .attr("opacity", 0.7);
//       }
//     });

//     // Draw markers for locations
//     const markerGroup = svg.append("g")
//       .attr("class", "markers");

//     recruitmentTransportLocations.forEach(location => {
//       const [x, y] = projection(location.coordinates);
      
//       const marker = markerGroup.append("g")
//         .attr("transform", `translate(${x}, ${y})`)
//         .attr("class", "marker")
//         .style("cursor", "pointer");
      
//       // Outer circle (white border)
//       marker.append("circle")
//         .attr("r", 8)
//         .attr("fill", "white");
      
//       // Inner circle (colored by type)
//       marker.append("circle")
//         .attr("r", 6)
//         .attr("fill", colorMap[location.type] || "#4B5563");
      
//       // Add event listeners
//       marker.on("mouseover", (event) => {
//         // Highlight marker
//         d3.select(event.currentTarget)
//           .select("circle:first-child")
//           .transition()
//           .duration(300)
//           .attr("r", 10);
        
//         d3.select(event.currentTarget)
//           .select("circle:nth-child(2)")
//           .transition()
//           .duration(300)
//           .attr("r", 8);
        
//         // Show tooltip
//         tooltip
//           .style("opacity", 1)
//           .style("left", (event.pageX + 10) + "px")
//           .style("top", (event.pageY - 28) + "px");
        
//         // Set selected location
//         setSelectedLocation(location);
//       });
      
//       marker.on("mouseout", (event) => {
//         // Reset marker size
//         d3.select(event.currentTarget)
//           .select("circle:first-child")
//           .transition()
//           .duration(300)
//           .attr("r", 8);
        
//         d3.select(event.currentTarget)
//           .select("circle:nth-child(2)")
//           .transition()
//           .duration(300)
//           .attr("r", 6);
        
//         // Hide tooltip
//         tooltip
//           .style("opacity", 0);
        
//         // Clear selected location
//         setSelectedLocation(null);
//       });
//     });

//     // Add legend
//     const legend = svg.append("g")
//       .attr("transform", `translate(20, ${height - 85})`);
    
//     const legendTypes = ["Recruitment Hub", "Transit Point", "Destination"];
    
//     legendTypes.forEach((type, i) => {
//       const legendItem = legend.append("g")
//         .attr("transform", `translate(0, ${i * 25})`);
      
//       legendItem.append("circle")
//         .attr("r", 6)
//         .attr("fill", colorMap[type]);
      
//       legendItem.append("text")
//         .attr("x", 15)
//         .attr("y", 4)
//         .text(type)
//         .attr("font-size", "12px")
//         .attr("fill", "#4B5563");
//     });

//     // Add title
//     svg.append("text")
//       .attr("x", width / 2)
//       .attr("y", 30)
//       .attr("text-anchor", "middle")
//       .attr("font-size", "18px")
//       .attr("font-weight", "bold")
//       .text("Trafficking Routes & Hotspots");

//   }, [worldData]);

//   if (isLoading) {
//     return (
//       <div className="w-full h-[500px] flex items-center justify-center bg-gray-100 rounded-lg shadow-lg">
//         <div className="text-gray-500">Loading map data...</div>
//       </div>
//     );
//   }

//   return (
//     <div className="relative w-full h-[500px] bg-white rounded-lg shadow-lg overflow-hidden">
//       <svg 
//         ref={svgRef} 
//         width={width} 
//         height={height} 
//         className="max-w-full" 
//         viewBox={`0 0 ${width} ${height}`}
//         style={{ margin: '0 auto' }}
//       />
      
//       {/* Tooltip */}
//       <div 
//         ref={tooltipRef}
//         className="absolute pointer-events-none bg-white p-2 rounded shadow-lg border border-gray-200 max-w-xs z-10 opacity-0 transition-opacity"
//         style={{ 
//           left: 0, 
//           top: 0, 
//           opacity: selectedLocation ? 1 : 0 
//         }}
//       >
//         {selectedLocation && (
//           <>
//             <h3 className="font-bold text-lg mb-1">{selectedLocation.name}</h3>
//             <p className="text-sm text-gray-600 mb-2">{selectedLocation.description}</p>
//             <span 
//               className="inline-block px-2 py-1 text-xs font-semibold rounded-full" 
//               style={{ 
//                 backgroundColor: `${colorMap[selectedLocation.type]}20`,
//                 color: colorMap[selectedLocation.type]
//               }}
//             >
//               {selectedLocation.type}
//             </span>
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default RecruitmentMap;